"use client";

import { c as _c } from "react/compiler-runtime";
import { Card, CardContent } from "@/components/rac/card";
import { type NearbyProperty } from "@/types/sup";
import { cn } from "@/utils/cn";
import currencify from "@/utils/currency/format";
import { s3Url } from "@/utils/s3-prefix";
import { toCdn } from "@/utils/to-cdn";
import { MapPin } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback } from "react";
import { DistanceTooltip } from "../deal-dashboard/distance-tooltip";
interface ProjectCardProps {
  data: NearbyProperty;
  className?: string;
  distanceFromText?: string;
  shouldPrioritizeCoverImage?: boolean; // Control whether to prioritize the first (cover) image
  shouldRedirect?: boolean; // Control whether to redirect to the project page
}
const formatLocation = (locality: string): string => {
  if (!locality) return "Location Not Specified";
  return locality.split("-").filter(Boolean).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};
const formatRange = (min: number, max: number, formatter?: (value: number) => string): string => {
  if (min === max) return formatter ? formatter(min) : min.toString();
  return formatter ? `${formatter(min)} - ${formatter(max)}` : `${min} - ${max}`;
};
export const ProjectCard = React.memo(function ProjectCard(t0) {
  const $ = _c(55);
  const {
    data,
    className,
    distanceFromText,
    shouldPrioritizeCoverImage: t1,
    shouldRedirect
  } = t0;
  const shouldPrioritizeCoverImage = t1 === undefined ? false : t1;
  const locality = data.localityName || "";
  let t2;
  if ($[0] !== locality) {
    t2 = formatLocation(locality);
    $[0] = locality;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const formattedLocation = t2;
  const name = data.projectName;
  let coverImage = data.cover;
  if (coverImage) {
    let t3;
    if ($[2] !== coverImage || $[3] !== data.projectId || $[4] !== data.type) {
      t3 = coverImage.startsWith("http") ? coverImage : data.type === "unit" || coverImage.startsWith("units/") ? s3Url(coverImage) : s3Url(`projects/${data.projectId}/images/${coverImage}`);
      $[2] = coverImage;
      $[3] = data.projectId;
      $[4] = data.type;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const rawImage = t3;
    let t4;
    if ($[6] !== rawImage) {
      t4 = toCdn(rawImage);
      $[6] = rawImage;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    coverImage = t4;
  }
  let t3;
  if ($[8] !== shouldRedirect) {
    t3 = e => {
      if (shouldRedirect) {
        return;
      } else {
        e.preventDefault();
      }
    };
    $[8] = shouldRedirect;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleCardClick = t3;
  const t4 = `/units/${data.slug ? `${data.slug}-` : ""}${data._id}`;
  let t5;
  if ($[10] !== className) {
    t5 = cn("h-full block", className);
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = coverImage || "/images/home/hot-projects/project-generic.svg";
  const t7 = name || "Project Cover Image";
  const t8 = coverImage || "/images/home/hot-projects/project-generic.svg";
  let t9;
  if ($[12] !== t8) {
    t9 = t8.endsWith(".svg");
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== shouldPrioritizeCoverImage || $[15] !== t6 || $[16] !== t7 || $[17] !== t9) {
    t10 = <Image src={t6} alt={t7} fill={true} className="object-cover transition-transform duration-500 group-hover:scale-105" sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50dvw, 33dvw" loading="lazy" priority={shouldPrioritizeCoverImage} unoptimized={t9} />;
    $[14] = shouldPrioritizeCoverImage;
    $[15] = t6;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== name) {
    t12 = <h3 className="font-semibold text-lg text-white line-clamp-2">{name}</h3>;
    $[20] = name;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <MapPin className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== formattedLocation) {
    t14 = <span className="truncate">{formattedLocation}</span>;
    $[23] = formattedLocation;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== data.distance || $[26] !== distanceFromText) {
    t15 = data.distance && <DistanceTooltip distance={data.distance} distanceFromText={distanceFromText} />;
    $[25] = data.distance;
    $[26] = distanceFromText;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <div className="flex items-center text-sm text-white/90">{t13}{t14}{t15}</div>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t16) {
    t17 = <div className="absolute bottom-0 left-0 right-0 p-2">{t12}{t16}</div>;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t10 || $[35] !== t17) {
    t18 = <div className="relative aspect-[4/3] w-full bg-muted/20 overflow-hidden">{t10}{t11}{t17}</div>;
    $[34] = t10;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== data.prices) {
    t19 = (data.prices || []).length > 0 && <div className="flex justify-between items-center"><span className="text-sm text-gray-600">Price</span><span className="text-sm font-medium text-primary-700">{formatRange(Math.min(...data.prices), Math.max(...data.prices), _temp)}</span></div>;
    $[37] = data.prices;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== data.bedrooms) {
    t20 = (data.bedrooms || []).length > 0 && <div className="flex justify-between items-center"><span className="text-sm text-gray-600">Bedrooms</span><span className="text-sm font-medium">{formatRange(Math.min(...data.bedrooms), Math.max(...data.bedrooms))} BHK</span></div>;
    $[39] = data.bedrooms;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== data.builtUpAreas) {
    t21 = (data.builtUpAreas || []).length > 0 && <div className="flex justify-between items-center"><span className="text-sm text-gray-600">Built-up Area</span><span className="text-sm font-medium">{formatRange(Math.min(...data.builtUpAreas), Math.max(...data.builtUpAreas), _temp2)}{" "}sqft</span></div>;
    $[41] = data.builtUpAreas;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t19 || $[44] !== t20 || $[45] !== t21) {
    t22 = <CardContent className="px-2 pb-2"><div className="space-y-2">{t19}{t20}{t21}</div></CardContent>;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t18 || $[48] !== t22) {
    t23 = <Card className="bg-white/80 hover:bg-white h-full transition-all duration-300 group cursor-pointer p-0 gap-3 shadow-none">{t18}{t22}</Card>;
    $[47] = t18;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== handleCardClick || $[51] !== t23 || $[52] !== t4 || $[53] !== t5) {
    t24 = <Link href={t4} target="_blank" className={t5} onClick={handleCardClick}>{t23}</Link>;
    $[50] = handleCardClick;
    $[51] = t23;
    $[52] = t4;
    $[53] = t5;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
});
function _temp(value) {
  return "\u20B9" + currencify({
    amount: value,
    abbreviated: true
  });
}
function _temp2(value_0) {
  return value_0.toLocaleString();
}